import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { playMessageSound, playMentionSound } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperPlaneRight, ChatsCircle, ShieldCheck } from "@phosphor-icons/react";

const parseUtc = (d) => {
  const iso = typeof d === "string" && !d.endsWith("Z") && !d.includes("+") ? `${d}Z` : d;
  return new Date(iso);
};
const fmtTime = (d) => parseUtc(d).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const fmtDay = (d) => parseUtc(d).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export default function Messaggi() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastTs = useRef(null);

  const markSeen = () => {
    localStorage.setItem("chat_last_seen", new Date().toISOString());
    api.post("/messages/mark-read").catch(() => {});
    window.dispatchEvent(new Event("chat-seen"));
  };

  const load = async (initial = false) => {
    try {
      const params = !initial && lastTs.current ? { after: lastTs.current } : {};
      const { data } = await api.get("/messages", { params });
      if (initial) setMessages(data);
      else if (data.length) setMessages((prev) => [...prev, ...data]);
      if (data.length) {
        lastTs.current = data[data.length - 1].created_at;
        if (!initial) {
          const fromOthers = data.filter((m) => m.sender_id !== user?.id);
          if (fromOthers.some((m) => (m.mentions || []).includes(user?.id))) playMentionSound();
          else if (fromOthers.length) playMessageSound();
        }
      }
      markSeen();
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load(true);
    const iv = setInterval(() => load(false), 4000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { text: text.trim() });
      setMessages((prev) => [...prev, data]);
      lastTs.current = data.created_at;
      setText("");
      markSeen();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  let lastDay = null;

  return (
    <div className="p-8 h-screen flex flex-col" data-testid="messaggi-page">
      <div className="mb-5">
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Comunicazioni di team</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Messaggi</h1>
      </div>

      <div className="flex-1 min-h-0 border border-border rounded-lg bg-card flex flex-col overflow-hidden">
        <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-message-list">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-3" data-testid="chat-empty">
              <ChatsCircle size={40} weight="duotone" />
              <p className="text-sm">Nessun messaggio: scrivi il primo per comunicare col team.</p>
            </div>
          )}
          {messages.map((m) => {
            const day = fmtDay(m.created_at);
            const showDay = day !== lastDay;
            lastDay = day;
            const own = m.sender_id === user?.id;
            return (
              <div key={m.id}>
                {showDay && (
                  <p className="text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground my-3">{day}</p>
                )}
                <div className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
                  <div className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
                    own ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
                  } ${(m.mentions || []).includes(user?.id) ? "ring-2 ring-amber-400" : ""}`}
                    data-mentioned={(m.mentions || []).includes(user?.id) ? "true" : undefined}>
                    {!own && (
                      <p className="text-[11px] font-bold mb-0.5 flex items-center gap-1.5">
                        {m.sender_name}
                        {m.sender_role === "admin" && <ShieldCheck size={12} className="text-primary" />}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                    <p className={`text-[10px] mt-1 text-right ${own ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {fmtTime(m.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="border-t border-border p-4 flex gap-3" data-testid="chat-form">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Scrivi un messaggio al team..."
            data-testid="chat-input"
            className="flex-1 px-4 py-3 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150"
          />
          <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-button"
            className="px-5 py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-40 transition-opacity duration-150 inline-flex items-center gap-2">
            <PaperPlaneRight size={17} weight="fill" /> Invia
          </button>
        </form>
      </div>
    </div>
  );
}
